import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import ButtonLink from '@components/button-link/ButtonLink';
import TeamMember from '@components/team-member/TeamMember';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { makeSeoData } from 'config/next-seo.config';
import { FAQPageJsonLd, NextSeo } from 'next-seo';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import { teamMembers } from './about.data';
import useStyles from './about.style';

const title = 'About Us | The easy button for listing a home';
const description = 'What is Guest House? A professional home staging company that sells the designer-curated home goods we use to help sell homes.';

export default function About() {
  const { theme } = useStyles();
  const { md } = useBreakpoints();

  const faqs = [
    {
      q: (
        <>
          How does staging work?
        </>
      ),
      a: (
        <>
          Simply tell us about your home. We&apos;ll then send you a free quote, availability and a contract to get going. Our team then delivers the furnishings, styles the space and photographs every corner so you can list on MLS, asap. We make everything easy and turnkey.
          <Button
            href="/home-staging"
            component={ButtonLink}
            variant="contained"
            color="primary"
            style={{ margin: theme.spacing(4, 0) }}
          >
            Learn More
          </Button>

        </>
      ),
    },
    {
      q: (
        <>
          How does shopping work?
        </>
      ),
      a: (
        <>
          We make it easy to purchase fresh products directly from hundreds of local makers and amazing brands. Shipping prices vary and orders deliver to your doorstep as quickly as one week.
        </>
      ),
    },
    {
      q: (
        <>
          Where is Guest House available?
        </>
      ),
      a: (
        <>
          Currently, you can stage homes with Guest House in Denver, Boulder, Vail and San Diego. You can shop or sell products from anywhere, with delivery across the US.
        </>
      ),
    }
  ];

  return (
    <>
      <AppBackgroundColor backgroundColor={theme.palette.pink.light} />

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description })} />

      <FAQPageJsonLd
        mainEntity={faqs.map(faq => {
          return {
            questionName: ReactDOMServer.renderToString(faq.q),
            acceptedAnswerText: ReactDOMServer.renderToString(faq.a),
          };
        })}
      />

      <ContentBlock maxWidth={1365}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={md ? 'center' : 'flex-start'}
          flexDirection={md ? 'row' : 'column'}
          style={{ marginBottom: md ? 0 : theme.spacing(3) }}
        >
          <Typography
            gutterBottom
            variant="h1"
          >
            The easy button for listing a home
          </Typography>
        </Box>

        <Box
          borderRadius="undefinedpx"
          maxHeight={600}
          overflow="hidden"
        >
          <video
            autoPlay
            loop
            muted
            width="100%"
            style={{ display: 'block' }}
          >
            <source
              src="/videos/Guesthouse Model_short_compressed.mp4"
              type="video/mp4"
            />
          </video>
        </Box>
      </ContentBlock>

      <ContentBlock
        maxWidth={880}
        margin={[12, 'auto', 24]}
      >
        <Typography
          fontSize="18px"
          textAlign="center"
        >
          At Guest House, we believe it should be easy to create a beautiful listing. Our platform automates everything agents and 
          sellers need—from staging to cleaning to moving and more—all in one place. On average, we reduce listing prep time by 50%, helping homes 
          hit the market faster and sell for top dollar. Now available in Colorado, San Diego, Orange County, Los Angeles and Phoenix/Scottsdale.
        </Typography>
      </ContentBlock>

      <ContentBlock
        maxWidth={1365}
        margin={[24, 'auto']}
      >
        <Box
          marginTop={theme.spacing(13)}
        >
          <Grid
            container
            spacing={4}
          >
            {teamMembers.map(member => {
              return (
                <Grid
                  key={member.name}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                >
                  <TeamMember
                    name={member.name}
                    title={member.title}
                    image={member.image}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </ContentBlock>
    </>
  );
}
